import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Form, Input, message, Row } from 'antd';

import { IFormValues, ILoginFormProps } from './login_form.types';
import styles from './login_form.module.scss';

const loginTranslationsPrefix = 'login_page';

const LoginForm: React.FC<ILoginFormProps> = ({ onLogin, onPasswordReset }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [didResetPassword, setDidResetPassword] = useState(false);

  const forgotButtonText = isForgotMode
    ? t(`${loginTranslationsPrefix}.have_password`)
    : t(`${loginTranslationsPrefix}.forgot_password`);

  function submitButtonText(): string {
    if (!isForgotMode) return t(`${loginTranslationsPrefix}.sign_in`);
    if (!didResetPassword) return t(`${loginTranslationsPrefix}.reset_password`);
    return t(`${loginTranslationsPrefix}.email_sent`);
  }

  const handleSubmit = async ({ email, password }: IFormValues): Promise<void> => {
    setSubmitting(true);
    try {
      if (!isForgotMode) {
        await onLogin(email, password);
      } else {
        await onPasswordReset(email);
      }
      if (isForgotMode) {
        message.success(t(`${loginTranslationsPrefix}.password_reset_email_sent`));
        setDidResetPassword(true);
        setSubmitting(false);
      }
    } catch (error) {
      message.error(t('general.no_access_error'));
      setSubmitting(false);
    }
  };

  const toggleForgotMode = (): void => {
    form.resetFields(['password']);
    setIsForgotMode(forgot => !forgot);
  };

  return (
    <Form
      name="login"
      className={styles.root}
      onFinish={handleSubmit}
      form={form}
      initialValues={{ email: '', password: '' }}
    >
      <Form.Item name="email" rules={[{ required: true, message: t(`${loginTranslationsPrefix}.please_input_email`) }]}>
        <Input
          autoFocus
          aria-label="email"
          type="email"
          size="large"
          prefix={<UserOutlined className={styles.input_icon} />}
          placeholder={t('general.other.email')}
        />
      </Form.Item>
      {!isForgotMode && (
        <Form.Item
          name="password"
          rules={[{ required: true, message: t(`${loginTranslationsPrefix}.please_input_password`) }]}
        >
          <Input.Password
            aria-label="password"
            size="large"
            prefix={<LockOutlined className={styles.input_icon} />}
            placeholder={t('general.other.password')}
          />
        </Form.Item>
      )}

      <Row justify="center">
        <Button
          className={styles.login_button}
          type="primary"
          htmlType="submit"
          size="large"
          shape="round"
          loading={isSubmitting}
          disabled={isForgotMode && didResetPassword}
        >
          {submitButtonText()}
        </Button>
      </Row>
      <Row justify="center">
        <Button type="link" disabled={isSubmitting} onClick={toggleForgotMode}>
          {forgotButtonText}
        </Button>
      </Row>
    </Form>
  );
};

export default LoginForm;
